/**
 * Technical Health check registry + evaluation helpers (Phase 3).
 *
 * The registry declares every baseline check once: its domain, its source
 * (an audit auditRunId + jobResults.checks key, or a manual entry), and the
 * CMA365 tier that first provides the capability (for gap-type classification).
 *
 * Status is NOT computed here from raw vendor data — that's the audit scripts'
 * job (results land in the `audit` Cosmos container). This module READS those
 * results plus the Knowledge directives (accountprofile) and derives the
 * per-check status and gap-type chip for display.
 *
 * Checks sourced from audits that don't have results yet (Phase 3 audits/routes
 * are a fast-follow) render as "no data yet" until their script runs.
 *
 * ── Phase 3 model ──────────────────────────────────────────────────────────
 * - Infrastructure & network use a CONFIGURATION + ALERTS model: "monitored /
 *   configured" checks (durable) are separate from "open alerts" checks; live
 *   up/down is NOT scored.
 * - Permission-gated M365 checks (Graph / Exchange Online) return a `notmanaged`
 *   status labelled "Microsoft 365 not managed by CMA Technology" when CMA lacks
 *   delegated access — not a fail. Public-DNS checks (SPF/DKIM/DMARC) are exempt.
 *
 * ── Audit contract (checkKeys each audit must emit in jobResults.checks) ─────
 *   INFRASTRUCTURE-AUDIT : firewallMonitored, ispMonitored, networkMonitored,
 *                          serversMonitored, vlanDocumented,
 *                          securitySubscription, fwIspAlerts, swApAlerts
 *   DATTO (per-device, datto-desktop/datto-server): diskHealth, antivirusHealth,
 *                          bitlockerStatus, tamperProtection, dnsFiltering, mdr, ntp, offline30d,
 *                          localAdmin, defaultCreds, screenLock, openAlerts,
 *                          lansweeperCoverage (CYB-A9 — Datto primary, Lansweeper reference),
 *                          patchServer (servers only), patchEndpoint (desktops)
 *   CAPACITY-AUDIT       : serverDisk, licenseUtil, ispBandwidth
 *   SECURITY-POSTURE-AUDIT (M365 Graph): mfaAll, mfaAdmin, mfaCloud,
 *                          passwordPolicy, inactiveAccounts, separateAdmin
 *   EMAIL-SECURITY-AUDIT (DNS): spfDkim, dmarc
 *   M365-SECURESCORE (M365 Defender, via Microsoft Secure Score - not Exchange Online PowerShell):
 *                          antiPhish, safeLinks (Safe Links + Safe Attachments combined)
 *   VULNERABILITY-AUDIT (Lansweeper): vulnScan, openVulns
 *   SONICWALL-AUDIT      : (answer-based AI) + noPublicMgmt, ipsEnabled, adminMfa, vpnOnly
 *   UNIFI                : (answer-based) + apInventory
 *   SECURITY-AWARENESS-AUDIT : enrollment, completion, phishing
 *   SERVOSITY            : imageCoverage, imageFailures, imageOffsite (image/server backups -
 *                          Story 491); desktopCoverage, desktopFailures, desktopOffsite
 *                          (desktop/laptop backups - Story 493); m365Coverage, m365Failures
 *                          (M365/C2C backups - Story 492)
 *   COVE                 : azureCoverage, azureFailures, azureOffsite (Azure-hosted server
 *                          backups - Story 494)
 *   ITGLUE-SSL-TRACKER   : sslMonitored, sslRenewals (Story 495 - supersedes the OBS-A1
 *                          team-action premise; ssl_certificates is a native IT Glue
 *                          resource, not a flexible asset)
 */

// ── Domains (the six accordions, in order) ──────────────────────────────────
export const DOMAINS = [
  { key: 'infrastructure', label: 'Infrastructure Health' },
  { key: 'capacity',       label: 'Capacity & Performance' },
  { key: 'cybersecurity',  label: 'Cybersecurity' },
  { key: 'compliance',     label: 'Compliance' },
  { key: 'dr',             label: 'Disaster Recovery' },
  { key: 'obsolescence',   label: 'Obsolescence & Lifecycle' },
];

// ── Tier ordering (classification field → gap-analysis tier) ────────────────
const TIER_ORDER = { Basic: 1, Enhanced: 2, Complete: 3 };

export function classificationToTier(classification) {
  const c = (classification || '').toLowerCase();
  if (c.includes('complete')) return 'Complete';
  if (c.includes('enhanced')) return 'Enhanced';
  if (c.includes('essential') || c.includes('basic')) return 'Basic';
  //No Service Plan, unknown → below Basic
  return 'None';
}

const tierRank = (t) => TIER_ORDER[t] || 0;

// ── Add-on catalog ──────────────────────────────────────────────────────────
// Capabilities the CMA365 wiki lists as purchasable add-ons below the tier that
// first includes them. A check tagged with `addon: <key>` is "in plan" if the
// customer's tier includes it OR they hold the matching entitlement directive.
// `bundle` (optional): add-ons sold as one package. They stay separate line items
// (so it's clear what's included) but their entitlement switches toggle together.
export const ADDONS = [
  { key: 'lan_monitoring', label: 'LAN monitoring (switches & APs)', bundle: 'network_addon' },
  { key: 'sonicwall_mgmt', label: 'SonicWALL firmware & management', bundle: 'network_addon' },
  { key: 'knowbe4',        label: 'Security awareness training (KnowBe4)' },
  { key: 'backup',         label: 'Server / M365 / endpoint backup (Servosity / Cove)' },
  { key: 'dmarc',          label: 'DMARC email protection' },
];

// ── The check registry ──────────────────────────────────────────────────────
// source.type: 'audit' (auditRunId [+ checkKey into jobResults.checks]) | 'manual'
// tier: the CMA365 tier that first provides the capability
// addon (optional): the add-on entitlement key that un-excludes the check below tier
// gapKind (optional): forces a gap type — 'customer_action' | 'addon' | 'licensing'
// valueKind (manual only): 'boolean' | 'date' | 'text'
export const CHECKS = [
  // ══ Infrastructure Health ══════════════════════════════════════════════════
  // Configuration ("is monitoring set up correctly?")
  { key: 'infra_firewall_monitored', domain: 'infrastructure', subcategory: 'Security Devices',
    label: 'Firewall monitored in PRTG (Ping + SNMP)',
    source: { type: 'audit', auditRunId: 'PRTG', checkKey: 'firewallMonitored' }, tier: 'Basic', dataAction: 'INF-A1' },
  { key: 'infra_isp_monitored', domain: 'infrastructure', subcategory: 'Networking',
    label: 'ISP circuit monitored in PRTG',
    source: { type: 'audit', auditRunId: 'PRTG', checkKey: 'ispMonitored' }, tier: 'Basic', dataAction: 'INF-A1' },
  { key: 'infra_network_monitored', domain: 'infrastructure', subcategory: 'Networking',
    label: 'Managed switches & APs monitored (Ping + SNMP)',
    source: { type: 'audit', auditRunId: 'PRTG', checkKey: 'networkMonitored' }, tier: 'Enhanced', addon: 'lan_monitoring', dataAction: 'INF-A1' },
  { key: 'infra_servers_monitored', domain: 'infrastructure', subcategory: 'Servers',
    label: 'Servers monitored by RMM',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'serversMonitored'}, tier: 'Basic' },
  { key: 'infra_rmm_coverage', domain: 'infrastructure', subcategory: 'Endpoints',
    label: 'Managed device inventory reviewed',
    source: { type: 'manual' }, tier: 'Basic', valueKind: 'date' },
  { key: 'infra_lansweeper_coverage', domain: 'infrastructure', subcategory: 'Endpoints',
    label: 'Lansweeper agent coverage',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'lansweeperCoverage' }, tier: 'Basic' },
  { key: 'infra_critical_services', domain: 'infrastructure', subcategory: 'Servers',
    label: 'Critical services monitored by RMM',
    source: { type: 'manual' }, tier: 'Basic', valueKind: 'boolean' },
  { key: 'infra_drive_health', domain: 'infrastructure', subcategory: 'Servers',
    label: 'Drive health monitored by RMM',
    source: { type: 'manual' }, tier: 'Basic', valueKind: 'boolean' },
  { key: 'infra_vlan_documented', domain: 'infrastructure', subcategory: 'Networking',
    label: 'VLAN documented & matches (NSM ↔ IT Glue)',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'vlanDocumented' }, tier: 'Enhanced' },
  { key: 'infra_security_subscription', domain: 'infrastructure', subcategory: 'Security Devices',
    label: 'Security subscription current',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'securitySubscription' }, tier: 'Basic' },
  // No dataAction (INF-D4): ntp emits FAIL, never NODATA, when udf35 is unreporting - so the
  // dataAction path is unreachable except when no DATTO record exists at all, where
  // "pending data action INF-D4" would wrongly mask "the audit hasn't run for this customer".
  { key: 'infra_ntp', domain: 'infrastructure', subcategory: 'Servers',
    label: 'NTP / time synchronization',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'ntp' }, tier: 'Basic' },
  // Alerts / state ("is anything unresolved?")
  { key: 'infra_fw_isp_alerts', domain: 'infrastructure', subcategory: 'Monitoring',
    label: 'Open firewall / ISP alerts (PRTG)',
    source: { type: 'audit', auditRunId: 'PRTG', checkKey: 'fwIspAlerts' }, tier: 'Basic', dataAction: 'INF-A1' },
  { key: 'infra_swap_alerts', domain: 'infrastructure', subcategory: 'Monitoring',
    label: 'Open switch / AP alerts (PRTG)',
    source: { type: 'audit', auditRunId: 'PRTG', checkKey: 'swApAlerts' }, tier: 'Enhanced', addon: 'lan_monitoring', dataAction: 'INF-A1' },
  { key: 'infra_open_alerts', domain: 'infrastructure', subcategory: 'Monitoring',
    label: 'Open alerts in RMM',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'openAlerts' }, tier: 'Basic' },
  { key: 'infra_offline_30d', domain: 'infrastructure', subcategory: 'Endpoints',
    label: 'No managed device offline >30 days',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'offline30d' }, tier: 'Basic' },

  // ══ Capacity & Performance ═════════════════════════════════════════════════
  { key: 'cap_server_disk', domain: 'capacity', subcategory: 'Storage',
    label: 'Server disk >20% free',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'serverDisk' }, tier: 'Basic' },
  { key: 'cap_endpoint_disk', domain: 'capacity', subcategory: 'Storage',
    label: 'Endpoint disk >20% free',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'diskHealth' }, tier: 'Basic' },
  { key: 'cap_m365_license', domain: 'capacity', subcategory: 'Licensing',
    label: 'M365 license utilization <90%',
    source: { type: 'audit', auditRunId: 'CSP-LICENSE', checkKey: 'licenseUtil' }, tier: 'Basic' },
  { key: 'cap_backup_storage', domain: 'capacity', subcategory: 'Backup',
    label: 'Backup storage has adequate headroom',
    source: { type: 'pending', note: 'Producer pending — backup producer (Servosity/Cove) not built yet' }, tier: 'Basic', addon: 'backup' },
  { key: 'cap_isp_bandwidth', domain: 'capacity', subcategory: 'Network',
    label: 'ISP bandwidth utilization healthy',
    source: { type: 'audit', auditRunId: 'PRTG-CAPACITY', checkKey: 'ispBandwidth'}, tier: 'Basic' },

  // ══ Cybersecurity — Identity & Access ══════════════════════════════════════
  { key: 'cyber_mfa_all', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'MFA registered for all users',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'mfaAll' }, tier: 'Enhanced' },
  { key: 'cyber_mfa_admin', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'MFA registered for admin accounts',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'mfaAdmin' }, tier: 'Enhanced' },
  { key: 'cyber_mfa_cloud', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'MFA enforced on cloud services',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'mfaCloud' }, tier: 'Enhanced' },
  { key: 'cyber_password_policy', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Password policy enforced (≥12 / history / lockout)',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'passwordPolicy' }, tier: 'Basic' },
  { key: 'cyber_inactive_accounts', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Inactive accounts cleaned up',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'inactiveAccounts' }, tier: 'Enhanced' },
  { key: 'cyber_separate_admin', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Separate admin accounts',
    source: { type: 'audit', auditRunId: 'M365-IDENTITY', checkKey: 'separateAdmin' }, tier: 'Basic' },
  { key: 'cyber_local_admin', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Local admin rights removed',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'localAdmin' }, tier: 'Basic' },
  { key: 'cyber_vendor_access', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Vendor remote access restricted',
    source: { type: 'manual' }, tier: 'Basic', valueKind: 'boolean' },
  { key: 'cyber_screen_lock', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Screen lock / session timeout enforced',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'screenLock' }, tier: 'Basic', dataAction: 'CYB-A1' },
  { key: 'cyber_default_creds', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Default credentials changed',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'defaultCreds' }, tier: 'Basic' },
  { key: 'cyber_access_review', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'User access rights reviewed (6 months)',
    source: { type: 'manual' }, tier: 'Enhanced', valueKind: 'date' },
  { key: 'cyber_offboarding', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'User offboarding within 24 hours',
    source: { type: 'manual' }, tier: 'Enhanced', valueKind: 'date' },
  { key: 'cyber_mdm', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Mobile devices managed (MDM)',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'cyber_remote_wipe', domain: 'cybersecurity', subcategory: 'Identity & Access',
    label: 'Remote wipe capability confirmed',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },

  // ══ Cybersecurity — Endpoint Protection ════════════════════════════════════
  { key: 'cyber_av_active', domain: 'cybersecurity', subcategory: 'Endpoint Protection',
    label: 'EDR/AV active, current & meets standard',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'antivirusHealth' }, tier: 'Basic' },
  { key: 'cyber_tamper', domain: 'cybersecurity', subcategory: 'Endpoint Protection',
    label: 'Tamper protection enabled',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'tamperProtection' }, tier: 'Basic', dataAction: 'CYB-A5' },
  { key: 'cyber_bitlocker', domain: 'cybersecurity', subcategory: 'Endpoint Protection',
    label: 'BitLocker encryption enabled on all devices',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'bitlockerStatus' }, tier: 'Basic' },
  { key: 'cyber_mdr', domain: 'cybersecurity', subcategory: 'Endpoint Protection',
    label: 'Managed Detection & Response (RocketCyber)',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'mdr' }, tier: 'Enhanced', dataAction: 'CYB-A4' },

  // ══ Cybersecurity — Patch Management ═══════════════════════════════════════
  { key: 'cyber_patch_server', domain: 'cybersecurity', subcategory: 'Patch Management',
    label: 'Server patch status (Datto)',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'patchServer' }, tier: 'Basic' },
  { key: 'cyber_patch_endpoint', domain: 'cybersecurity', subcategory: 'Patch Management',
    label: 'Desktop / laptop patch status (Datto)',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'patchEndpoint' }, tier: 'Basic' },
  { key: 'cyber_patch_thirdparty', domain: 'cybersecurity', subcategory: 'Patch Management',
    label: 'Third-party application patching',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'patch3rdParty' }, tier: 'Enhanced'},

  // ══ Cybersecurity — Email Security ═════════════════════════════════════════
  { key: 'cyber_spf_dkim', domain: 'cybersecurity', subcategory: 'Email Security',
    label: 'SPF & DKIM configured',
    source: { type: 'audit', auditRunId: 'DNS', checkKey: 'spfDkim' }, tier: 'Basic' },
  { key: 'cyber_dmarc', domain: 'cybersecurity', subcategory: 'Email Security',
    label: 'DMARC configured',
    source: { type: 'audit', auditRunId: 'DNS', checkKey: 'dmarc' }, tier: 'Enhanced', addon: 'dmarc' },
  { key: 'cyber_antiphish', domain: 'cybersecurity', subcategory: 'Email Security',
    label: 'Anti-phishing / anti-spam (M365 Defender)',
    source: { type: 'audit', auditRunId: 'M365-SECURESCORE', checkKey: 'antiPhish' }, tier: 'Basic' },
  { key: 'cyber_safelinks', domain: 'cybersecurity', subcategory: 'Email Security',
    label: 'Safe Links / Safe Attachments (M365 Defender)',
    source: { type: 'audit', auditRunId: 'M365-SECURESCORE', checkKey: 'safeLinks' }, tier: 'Basic' },

  // ══ Cybersecurity — Network Security ═══════════════════════════════════════
  { key: 'cyber_firewall_config', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Firewall firmware current',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'firmwareVersion' }, tier: 'Enhanced', addon: 'sonicwall_mgmt' },
  { key: 'cyber_fw_no_public_mgmt', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Firewall management not publicly exposed',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'noPublicMgmt' }, tier: 'Enhanced', addon: 'sonicwall_mgmt' },
  { key: 'cyber_fw_ips', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'IPS / threat protection enabled',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'ipsEnabled' }, tier: 'Enhanced', addon: 'sonicwall_mgmt' },
  { key: 'cyber_fw_admin_mfa', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Firewall admin MFA enabled',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'adminMfa' }, tier: 'Enhanced', addon: 'sonicwall_mgmt' },
  { key: 'cyber_vpn_only', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Remote access via VPN only (no exposed RDP)',
    source: { type: 'audit', auditRunId: 'SONICWALL', checkKey: 'vpnOnly' }, tier: 'Enhanced', addon: 'sonicwall_mgmt' },
  { key: 'cyber_dns_filtering', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'DNS / web content filtering (EDR policy configured)',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'dnsFiltering' }, tier: 'Basic' },
  { key: 'cyber_tls_transit', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Encryption in transit (TLS 1.2+)',
    source: { type: 'manual' }, tier: 'Enhanced', valueKind: 'boolean' },
  { key: 'cyber_vuln_scan', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Vulnerability scan within 90 days',
    source: { type: 'audit', auditRunId: 'LANSWEEPER', checkKey: 'vulnScan' }, tier: 'Enhanced' },
  { key: 'cyber_open_vulns', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Open vulnerabilities tracked & assigned',
    source: { type: 'audit', auditRunId: 'LANSWEEPER', checkKey: 'openVulns' }, tier: 'Enhanced' },
  { key: 'cyber_ap_inventory', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Wireless AP inventory maintained',
    source: { type: 'audit', auditRunId: 'UNIFI', checkKey: 'apInventory' }, tier: 'Enhanced', addon: 'lan_monitoring' },
  { key: 'cyber_guest_segmented', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Guest network segmented',
    source: { type: 'audit', auditRunId: 'UNIFI', checkKey: 'guestNetworkSegregated' }, tier: 'Enhanced' },
  { key: 'cyber_pentest_external', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'External penetration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', gapKind: 'addon', valueKind: 'date' },
  { key: 'cyber_pentest_internal', domain: 'cybersecurity', subcategory: 'Network Security',
    label: 'Internal penetration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', gapKind: 'addon', valueKind: 'date' },

  // ══ Cybersecurity — Security Awareness ═════════════════════════════════════
  { key: 'cyber_kb4_enrolled', domain: 'cybersecurity', subcategory: 'Security Awareness',
    label: 'All users enrolled in security awareness training',
    source: { type: 'audit', auditRunId: 'KNOWBE4', checkKey: 'enrollment' }, tier: 'Enhanced', addon: 'knowbe4' },
  { key: 'cyber_kb4_completion', domain: 'cybersecurity', subcategory: 'Security Awareness',
    label: 'Training completion ≥90%',
    source: { type: 'audit', auditRunId: 'KNOWBE4', checkKey: 'completion' }, tier: 'Enhanced', addon: 'knowbe4' },
  { key: 'cyber_phishing_sim', domain: 'cybersecurity', subcategory: 'Security Awareness',
    label: 'Phishing simulation click rate within tolerance',
    source: { type: 'audit', auditRunId: 'KNOWBE4', checkKey: 'phishing' }, tier: 'Enhanced', addon: 'knowbe4' },

  // ══ Cybersecurity — Monitoring & Logging (Complete-only, all manual) ═══════
  { key: 'cyber_audit_logging', domain: 'cybersecurity', subcategory: 'Monitoring & Logging',
    label: 'Audit logging enabled on servers / sensitive systems',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'cyber_log_retention', domain: 'cybersecurity', subcategory: 'Monitoring & Logging',
    label: '12-month log retention',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'cyber_log_review', domain: 'cybersecurity', subcategory: 'Monitoring & Logging',
    label: 'Log review process in place',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'date' },
  { key: 'cyber_fim', domain: 'cybersecurity', subcategory: 'Monitoring & Logging',
    label: 'File / system integrity monitoring',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'cyber_saas_alerts', domain: 'cybersecurity', subcategory: 'Monitoring & Logging',
    label: 'Compromised-account / DLP detection (SaaS Alerts)',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },

  // ══ Compliance — Governance ════════════════════════════════════════════════
  { key: 'comp_security_policy', domain: 'compliance', subcategory: 'Governance',
    label: 'Cybersecurity policy documented',
    source: { type: 'manual' }, tier: 'Complete', gapKind: 'customer_action', valueKind: 'date' },
  { key: 'comp_aup', domain: 'compliance', subcategory: 'Governance',
    label: 'Acceptable Use Policy documented & signed',
    source: { type: 'manual' }, tier: 'Complete', gapKind: 'customer_action', valueKind: 'date' },
  { key: 'comp_risk_assessment', domain: 'compliance', subcategory: 'Governance',
    label: 'Annual security risk assessment',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'date' },
  { key: 'comp_data_inventory', domain: 'compliance', subcategory: 'Governance',
    label: 'Sensitive data inventory maintained',
    source: { type: 'manual' }, tier: 'Complete', gapKind: 'customer_action', valueKind: 'date' },

  // ══ Disaster Recovery — Backup & Recovery (4 types × 5 checks) ═════════════
  // Image backups (Servosity)
  { key: 'dr_image_coverage', domain: 'dr', subcategory: 'Image backups (Servosity)',
    label: 'Critical servers backed up (image)',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'imageCoverage' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_image_failures', domain: 'dr', subcategory: 'Image backups (Servosity)',
    label: 'No image-backup failures unresolved >24h',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'imageFailures' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_image_offsite', domain: 'dr', subcategory: 'Image backups (Servosity)',
    label: 'Image offsite / cloud copy',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'imageOffsite' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_image_immutable', domain: 'dr', subcategory: 'Image backups (Servosity)',
    label: 'Image immutable backup confirmed',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'boolean' },
  { key: 'dr_image_restore', domain: 'dr', subcategory: 'Image backups (Servosity)',
    label: 'Image restoration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'date' },
  // M365 backups (Servosity)
  { key: 'dr_m365_coverage', domain: 'dr', subcategory: 'M365 backups (Servosity)',
    label: 'M365 backed up',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'm365Coverage' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_m365_failures', domain: 'dr', subcategory: 'M365 backups (Servosity)',
    label: 'No M365-backup failures unresolved >24h',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'm365Failures' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_m365_immutable', domain: 'dr', subcategory: 'M365 backups (Servosity)',
    label: 'M365 immutable backup confirmed',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'boolean' },
  { key: 'dr_m365_restore', domain: 'dr', subcategory: 'M365 backups (Servosity)',
    label: 'M365 restoration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'date' },
  // Desktop / laptop backups (Servosity) — add-on all tiers
  { key: 'dr_desktop_coverage', domain: 'dr', subcategory: 'Desktop / laptop backups (Servosity)',
    label: 'Desktops / laptops backed up',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'desktopCoverage' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_desktop_failures', domain: 'dr', subcategory: 'Desktop / laptop backups (Servosity)',
    label: 'No desktop-backup failures unresolved >24h',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'desktopFailures' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_desktop_offsite', domain: 'dr', subcategory: 'Desktop / laptop backups (Servosity)',
    label: 'Desktop offsite / cloud copy',
    source: { type: 'audit', auditRunId: 'SERVOSITY', checkKey: 'desktopOffsite' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_desktop_immutable', domain: 'dr', subcategory: 'Desktop / laptop backups (Servosity)',
    label: 'Desktop immutable backup confirmed',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'boolean' },
  { key: 'dr_desktop_restore', domain: 'dr', subcategory: 'Desktop / laptop backups (Servosity)',
    label: 'Desktop restoration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'date' },
  // Azure-hosted server backups (Cove)
  { key: 'dr_azure_coverage', domain: 'dr', subcategory: 'Azure-hosted server backups (Cove)',
    label: 'Azure-hosted servers backed up (image)',
    source: { type: 'audit', auditRunId: 'COVE', checkKey: 'azureCoverage' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_azure_failures', domain: 'dr', subcategory: 'Azure-hosted server backups (Cove)',
    label: 'No Azure-backup failures unresolved >24h',
    source: { type: 'audit', auditRunId: 'COVE', checkKey: 'azureFailures' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_azure_offsite', domain: 'dr', subcategory: 'Azure-hosted server backups (Cove)',
    label: 'Azure offsite / cloud copy',
    source: { type: 'audit', auditRunId: 'COVE', checkKey: 'azureOffsite' }, tier: 'Enhanced', addon: 'backup' },
  { key: 'dr_azure_immutable', domain: 'dr', subcategory: 'Azure-hosted server backups (Cove)',
    label: 'Azure immutable backup confirmed',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'boolean' },
  { key: 'dr_azure_restore', domain: 'dr', subcategory: 'Azure-hosted server backups (Cove)',
    label: 'Azure restoration test within 12 months',
    source: { type: 'manual' }, tier: 'Enhanced', addon: 'backup', valueKind: 'date' },
  // Overall
  { key: 'dr_rto_rpo', domain: 'dr', subcategory: 'Backup & Recovery',
    label: 'RTO & RPO defined',
    source: { type: 'manual' }, tier: 'Enhanced', gapKind: 'customer_action', valueKind: 'boolean' },

  // ══ Disaster Recovery — IR Planning & Business Continuity ═══════════════════
  { key: 'dr_ir_plan', domain: 'dr', subcategory: 'Incident Response',
    label: 'IR plan exists & reviewed (12 months)',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'date' },
  { key: 'dr_cyber_insurance', domain: 'dr', subcategory: 'Incident Response',
    label: 'Cyber insurance carrier documented',
    source: { type: 'manual' }, tier: 'Basic', gapKind: 'customer_action', valueKind: 'date' },
  { key: 'dr_breach_notification', domain: 'dr', subcategory: 'Incident Response',
    label: 'Breach-notification obligations documented',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'dr_bc_plan', domain: 'dr', subcategory: 'Business Continuity',
    label: 'Business continuity plan reviewed (12 months)',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'date' },
  { key: 'dr_critical_systems', domain: 'dr', subcategory: 'Business Continuity',
    label: 'Critical systems identified',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },
  { key: 'dr_key_person', domain: 'dr', subcategory: 'Business Continuity',
    label: 'Key-person dependencies identified',
    source: { type: 'manual' }, tier: 'Complete', valueKind: 'boolean' },

  // ══ Obsolescence & Lifecycle ═══════════════════════════════════════════════
  { key: 'obs_os_eol', domain: 'obsolescence', subcategory: 'Software',
    label: 'No OS past Microsoft end-of-support',
    source: { type: 'audit', auditRunId: 'DATTO', checkKey: 'osEol' }, tier: 'Basic' },
  { key: 'obs_device_eol', domain: 'obsolescence', subcategory: 'Hardware',
    label: 'No devices past manufacturer EOL (Lansweeper)',
    source: { type: 'audit', auditRunId: 'LANSWEEPER', checkKey: 'deviceEol' }, tier: 'Basic', dataAction: 'CYB-A9' },
  { key: 'obs_contract_renewals', domain: 'obsolescence', subcategory: 'Renewals',
    label: 'Contract renewals visible 90 days out',
    source: { type: 'audit', auditRunId: 'AUTOTASK-CONTRACT', checkKey: 'contractRenewals' }, tier: 'Basic' },
  { key: 'obs_domain_renewals', domain: 'obsolescence', subcategory: 'Renewals',
    label: 'Domain renewals visible 90 days out',
    source: { type: 'audit', auditRunId: 'ITGLUE-DOMAIN-TRACKER', checkKey: 'domainRenewals' }, tier: 'Basic' },
  { key: 'obs_ssl_monitored', domain: 'obsolescence', subcategory: 'Renewals',
    label: 'SSL certificates monitored',
    source: { type: 'audit', auditRunId: 'ITGLUE-SSL-TRACKER', checkKey: 'sslMonitored' }, tier: 'Basic' },
  { key: 'obs_ssl_renewals', domain: 'obsolescence', subcategory: 'Renewals',
    label: 'SSL certificate renewals visible 90 days out',
    source: { type: 'audit', auditRunId: 'ITGLUE-SSL-TRACKER', checkKey: 'sslRenewals' }, tier: 'Basic' },
];

// ── Grouping ────────────────────────────────────────────────────────────────
// Group checks by subcategory in FIRST-APPEARANCE order, not alphabetically:
// the registry's own order IS the intended reading order (Infrastructure reads
// Security Devices → Networking → Servers → Endpoints, which alphabetising
// would scramble). A check with no subcategory groups under '' and renders
// ungrouped at the top.
//
// Shared so the Technical Health tab and the fleet report's check picker cannot
// drift into showing the same catalog in two different orders.
export function groupBySubcategory(checks) {
  const groups = [];
  const idx = {};
  for (const c of checks) {
    const sub = c.subcategory || '';
    if (!(sub in idx)) {
      idx[sub] = groups.length;
      groups.push({ sub, items: [] });
    }
    groups[idx[sub]].items.push(c);
  }
  return groups;
}

// ── Status helpers ──────────────────────────────────────────────────────────
// status values: 'pass' | 'warn' | 'fail' | 'manual' (unrecorded) |
//                'nodata' (audit ran, no result yet) | 'excluded' (capability not in plan) |
//                'notmanaged' (M365 not managed by CMA — NOOP, not a fault) |
//                'notapplicable' (audit ran, correctly found nothing to evaluate — not a
//                fault, not pending; distinct from 'nodata' which means data is MISSING) |
//                'pending' (producer that emits this check isn't live yet — not a fault)

const norm = (v) => (v == null ? '' : String(v)).trim().toUpperCase();

// Result tokens an audit can emit to mean "Microsoft 365 not managed by CMA".
const isNoop = (r) => { const R = norm(r); return R === 'NOOP' || R === 'NOTMANAGED' || R === 'NOT-MANAGED'; };

// Result token a fan-out producer emits when it ran but found zero applicable targets
// (e.g. zero Windows servers at a site) - a legitimate outcome, not a gap and not a
// fault, so it must never roll up as 'pass' (nothing was actually verified) or 'nodata'
// (which reads as "audit pending" - misleading when the audit definitely ran).
const isNotApplicable = (r) => norm(r) === 'NOTAPPLICABLE';

export const NOTMANAGED_MESSAGE = 'Microsoft Graph requires Reports.Read.All / AuditLog.Read.All permissions';

function statusFromAudit(records, checkKey) {
  if (!records || records.length === 0) return { status: 'nodata', value: null, message: null };
  let pass = 0, warn = 0, fail = 0, noop = 0, notApplicable = 0;
  const messages = [];
  const nodataMessages = [];
  let noopMsg = null;
  let naMsg = null;
  let naTenantWide = false;
  let naParentAccount = null;
  for (const r of records) {
    let res;
    if (checkKey) {
      const c = r.jobResults && r.jobResults.checks ? r.jobResults.checks[checkKey] : null;
      res = c ? c.result : null;
      if (c && c.message && (c.result === 'WARN' || c.result === 'FAIL')) messages.push(c.message);
      // Kept separate from messages[] above rather than folded in directly: if any
      // record actually scores (fail/warn/pass), NODATA text from other records would
      // just dilute the relevant message. Only surfaces below when NOTHING scored.
      if (c && c.message && c.result === 'NODATA') nodataMessages.push(c.message);
      // Carry the NOOP reason (e.g. "No firewall on file") so it surfaces instead
      // of the generic not-managed message. M365 audits set this to the Graph text,
      // so their display is unchanged.
      if (c && c.message && isNoop(c.result) && !noopMsg) noopMsg = c.message;
      if (c && c.message && isNotApplicable(c.result) && !naMsg) naMsg = c.message;
      // Producers set tenantWide on a NOTAPPLICABLE check whose real answer lives on the
      // PARENT account: a child location shares its parent's M365 tenant, so Conditional
      // Access / admin roles / Secure Score have no per-location value. Distinct from
      // NOTAPPLICABLE meaning "ran, found nothing to evaluate", where there is nowhere
      // to send the reader.
      if (c && isNotApplicable(c.result) && c.tenantWide && !naTenantWide) {
        naTenantWide = true;
        naParentAccount = c.parentAccount || null;
      }
    } else {
      res = r.answer;
    }
    if (isNoop(res)) { noop += 1; continue; }
    if (isNotApplicable(res)) { notApplicable += 1; continue; }
    const R = norm(res);
    if (R === 'FAIL') fail += 1;
    else if (R === 'WARN' || R === 'WARNING') warn += 1;
    else if (R === 'PASS') pass += 1;
  }
  const scored = pass + warn + fail;
  // Every contributing record NOOPed (and there was at least one) → not managed.
  if (scored === 0 && noop > 0) return { status: 'notmanaged', value: null, message: noopMsg || NOTMANAGED_MESSAGE };
  // Every contributing record was "ran, nothing applicable" → surface that distinctly
  // rather than falling through to the generic nodata/"Audit pending" bucket below.
  if (scored === 0 && notApplicable > 0) {
    return { status: 'notapplicable', value: null, message: naMsg,
             tenantWide: naTenantWide, parentAccount: naParentAccount };
  }
  const status = fail > 0 ? 'fail' : warn > 0 ? 'warn' : pass > 0 ? 'pass' : 'nodata';
  // Records exist but nothing scored (e.g. an undocumented circuit, an unreporting
  // UDF) is a DIFFERENT condition from records.length === 0 above (the audit never
  // ran at all) - the producer clearly ran, there's just nothing to grade yet. Surface
  // its own NODATA message here so the caller can show that instead of a generic
  // "hasn't run" placeholder that would otherwise be indistinguishable from the
  // genuine never-ran case.
  const effectiveMessages = status === 'nodata' && messages.length === 0 ? nodataMessages : messages;
  return {
    status,
    // Only show the "X/Y passing" tally for multi-target (per-device) audits;
    // for account-level single-record audits the check message is clearer.
    value: scored > 1 ? `${pass}/${scored} passing` : null,
    message: [...new Set(effectiveMessages)].slice(0, 3).join(' · ') || null,
  };
}

export function statusFromManual(record) {
  if (!record) return { status: 'manual', value: null, message: null };
  const v = record.value;
  if (record.value_type === 'boolean' || typeof v === 'boolean') {
    const truthy = v === true || norm(v) === 'TRUE' || norm(v) === 'YES';
    return { status: truthy ? 'pass' : 'fail', value: truthy ? 'Yes' : 'No', message: record.notes || null };
  }
  // date / text — a recorded value counts as satisfied for now (date-window
  // scoring can be layered on later). Unrecorded handled above.
  return { status: 'pass', value: String(v), message: record.notes || null };
}

// The grades an override value may collapse to. Mirrors _OVERRIDE_VALUE in
// cma-api routes/cma/audit/_directives.py — the two must agree, or a directive
// the server honours would be ignored here, or vice versa.
const OVERRIDE_GRADE = {
  PASS: 'pass', YES: 'pass', TRUE: 'pass',
  WARN: 'warn', WARNING: 'warn',
  FAIL: 'fail', NO: 'fail', FALSE: 'fail',
};

/** The status an override value forces, or null if the value means nothing. */
export function overrideGrade(value) {
  return OVERRIDE_GRADE[norm(value)] || null;
}

// Expiry is decided HERE, not server-side, because a stored override now reaches
// the UI without passing through _directives.py's own filter. The dialog promises
// "Reverts to the automated result on this date", so the date itself is past-due.
// Unparseable → kept, matching _directives.py's explicit behaviour.
export function isOverrideExpired(expires, now = Date.now()) {
  if (!expires) return false;
  const exp = new Date(expires);
  if (isNaN(exp.getTime())) return false;
  return exp.getTime() < now;
}

function applyOverride(base, override) {
  if (!override) return base;
  if (isOverrideExpired(override.override_expires)) return base; // expired → revert
  const status = overrideGrade(override.value);
  // An unmappable value is not an override at all — matches _directives.py, which
  // skips the directive rather than recording a no-op one. Otherwise hasExplicit
  // below would go true with nothing on screen to explain it.
  if (!status) return base;
  return { ...base, status, value: override.value, overridden: true, override };
}

// Re-derive a check's status from its per-target detail rows, excluding any
// suppressed targets (e.g. a decommissioned firewall recorded in Knowledge).
// Only kicks in when there's an actual suppression, so normal display is
// unchanged. Mirrors the audit's own rollup: any FAIL→fail, any WARN→warn.
function applySuppress(base, records, checkKey, suppressTargets) {
  let pass = 0, warn = 0, fail = 0, total = 0, suppressed = 0;
  for (const r of records) {
    const c = checkKey && r.jobResults && r.jobResults.checks ? r.jobResults.checks[checkKey] : null;
    const items = (c && Array.isArray(c.details) && c.details.length)
      ? c.details.map(d => ({ target: d.target, result: d.result }))
      : [{ target: r.title || r.account || r.id, result: checkKey ? (c ? c.result : null) : r.answer }];
    for (const it of items) {
      if (suppressTargets.has(String(it.target))) { suppressed += 1; continue; }
      total += 1;
      const R = norm(it.result);
      if (R === 'FAIL') fail += 1;
      else if (R === 'WARN' || R === 'WARNING') warn += 1;
      else if (R === 'PASS') pass += 1;
    }
  }
  if (suppressed === 0) return base;
  const status = fail > 0 ? 'fail' : warn > 0 ? 'warn' : pass > 0 ? 'pass' : 'nodata';
  // Keep the audit's own rollup message (e.g. "0% click rate … 5 of 8 not enrolled")
  // when suppression doesn't change the outcome — don't replace it with the
  // per-row detail messages. Only fall back to a count summary when the suppression
  // actually flips the status (so a stale fail message isn't shown on a now-passing
  // check). The "N suppressed" count is surfaced separately as a chip.
  const summary = `${pass}/${total} OK · ${suppressed} suppressed`;
  const message = (status === base.status && base.message) ? base.message : summary;
  return { ...base, status, value: total > 1 ? `${pass}/${total} passing` : null, message, suppressed };
}

// Is the capability in the customer's plan? True if their tier includes it, or
// they hold the matching add-on entitlement.
function inPlan(check, tier, entitlements) {
  const includedByTier = !check.tier || tierRank(check.tier) <= tierRank(tier);
  const hasAddon = check.addon && entitlements && entitlements.has(check.addon);
  return includedByTier || hasAddon;
}

// Gap-type for a failing/warning/excluded check, given the customer's plan.
export function gapTypeFor(check, tier, entitlements) {
  if (check.gapKind === 'customer_action') return { label: 'Customer action required', color: 'default' };
  if (check.gapKind === 'licensing')       return { label: 'License required', color: 'warning' };
  if (check.gapKind === 'addon')           return { label: 'Add-on available', color: 'warning' };
  // Not in the customer's plan: an add-on they could buy, or a tier upgrade.
  if (!inPlan(check, tier, entitlements)) {
    if (check.addon) return { label: 'Add-on available', color: 'warning' };
    return { label: `Requires ${check.tier}`, color: 'warning' };
  }
  // In their plan but not passing → configuration gap.
  return { label: 'Not configured', color: 'error' };
}

/**
 * Evaluate one check against the loaded audit + Knowledge directive data.
 * `entitlements` is a Set of add-on keys the customer holds.
 * Returns { status, value, message, overridden, override, manualRecord, gap, serviceGap }.
 */
export function evaluateCheck(check, auditByRunId, manualByKey, overrideByKey, tier, entitlements, suppressByCheck) {
  let base;
  if (check.source.type === 'audit') {
    const records = auditByRunId[check.source.auditRunId] || [];
    base = statusFromAudit(records, check.source.checkKey);
    const suppressTargets = suppressByCheck && suppressByCheck[check.source.checkKey];
    if (suppressTargets && suppressTargets.size) {
      base = applySuppress(base, records, check.source.checkKey, suppressTargets);
    }
  } else if (check.source.type === 'pending') {
    // The producer that will emit this check isn't live yet. Surface an explicit,
    // neutral "pending" state — never a misleading no-data / fail. It still flows
    // through applyOverride and the not-in-plan branch below, so a vCIO override or
    // manual value opts it back in, and a customer without the capability at all is
    // shown as excluded rather than pending.
    base = { status: 'pending', value: null, message: check.source.note || null };
  } else {
    base = statusFromManual(manualByKey[check.key]);
  }

  const result = applyOverride(base, overrideByKey[check.source.checkKey]);
  result.manualRecord = manualByKey[check.key] || null;
  // Copied explicitly rather than trusting applyOverride to spread unknown fields through.
  result.tenantWide = !!base.tenantWide;
  result.parentAccount = base.parentAccount || null;

  // M365 not managed by CMA → NOOP. Surface it as not-applicable, never a fault,
  // unless a vCIO override or manual value explicitly opts the check back in.
  const hasManual = !!manualByKey[check.key];
  const hasExplicit = !!overrideByKey[check.source.checkKey] || hasManual;
  if (result.status === 'notmanaged' && !hasExplicit) {
    result.value = null;
    result.message = result.message || NOTMANAGED_MESSAGE;
    result.notManaged = true;
    return result;
  }

  // Capability not in the customer's plan: don't surface audit pass/fail health —
  // it isn't a service we provide them. Show it as excluded with a service-gap /
  // add-on chip instead.
  //
  // The plan OUTRANKS a vCIO override, deliberately. An override asserts a service
  // level we don't sell this customer; honouring it would both score them on a
  // service they don't have and clear the add-on chip, erasing the upsell signal
  // this row exists to carry. A manual value still opts the check back in — a
  // manual check has no automated data, so excluding it leaves the row permanently
  // blank rather than hiding anything.
  if (!inPlan(check, tier, entitlements) && !hasManual) {
    result.status = 'excluded';
    result.value = null;
    result.message = null;
    result.serviceGap = true;
    result.gap = gapTypeFor(check, tier, entitlements);
    // The override row is kept in Cosmos and takes effect again if the customer
    // buys the tier/add-on. Hand it to the drawer so a vCIO can see why theirs
    // isn't showing, rather than concluding it failed to save.
    result.suppressedOverride = result.override || null;
    result.overridden = false;
    result.override = null;
    return result;
  }

  // Producer for this check isn't live yet (and no override/manual value opted it
  // back in): carry the "why" note for the tooltip, no gap chip. Kept after the
  // not-in-plan branch so a customer without the capability shows excluded instead.
  if (result.status === 'pending') {
    result.pending = true;
    result.note = check.source.note || null;
    result.gap = null;
    return result;
  }

  // Audit-pending check whose data source is gated on a known team data action:
  // surface the action id instead of a generic "audit pending" - but only when
  // statusFromAudit had nothing better to say (records.length === 0, genuinely never
  // ran). Same bug class as the TechnicalHealthTab "hasn't run" caption: don't let a
  // generic fallback clobber a real per-record NODATA message just because the rolled
  // -up status happens to be 'nodata' too - e.g. screenLock can have 42 real per-device
  // results, each explaining "not reporting - pending CYB-A1", which is more useful
  // than the generic text and should win.
  if (result.status === 'nodata' && check.dataAction && !hasExplicit && !result.message) {
    result.dataAction = check.dataAction;
    result.message = `Audit results pending data action ${check.dataAction}`;
  }

  // Gap chip only for genuine plan / action gaps — add-on, tier upgrade,
  // licensing, or customer action (the explicit gapKinds). An in-plan check that
  // is simply failing or warning is a live health finding: its status icon and
  // message already convey it, so it gets no chip.
  result.gap = (result.status === 'fail' || result.status === 'warn') && check.gapKind
    ? gapTypeFor(check, tier, entitlements)
    : null;
  return result;
}

// Domain roll-up: counts + a simple pass% over checks that have a definite result.
// 'excluded', 'notmanaged', and 'notapplicable' never count toward the score.
export function summarizeDomain(evaluations) {
  let pass = 0, scored = 0, failing = 0;
  for (const e of evaluations) {
    if (e.status === 'fail' || e.status === 'warn') failing += 1;
    if (e.status === 'pass' || e.status === 'fail' || e.status === 'warn') {
      scored += 1;
      if (e.status === 'pass') pass += 1;
    }
  }
  return { failing, scored, pass, pct: scored > 0 ? Math.round((pass / scored) * 100) : null };
}
